import { useState } from 'react';
import { Link } from './Link';
import { Button, cn } from './ui';
import { useCookieConsent } from '../lib/cookieConsent';

/**
 * GDPR cookie-consent UI for the public site. Two modes off one component:
 *   - Banner: first-visit bottom bar (Accept / Reject / Manage). Only shown when
 *     there's actually a non-essential cookie to consent to (GA configured) and no
 *     decision has been made yet.
 *   - Preferences panel: opened via "Manage" or the footer "Cookie settings" link;
 *     a locked "necessary" row + an analytics toggle.
 *
 * `trackersConfigured` gates the auto-banner: if no consent-requiring tracker (GA /
 * Clarity / PostHog) is enabled there are no non-essential cookies, so we never nag.
 * The panel can still be opened from the footer at any time to review/withdraw.
 *
 * `providers` is the list of analytics tools the super-admin currently has enabled (e.g.
 * ["Google Analytics", "PostHog"]). The copy names only these, so disabling one in
 * Admin → Analytics immediately stops the banner from mentioning it. Empty → generic copy.
 */
function joinNames(names: string[]): string {
  if (names.length === 0) return 'analytics tools';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export function CookieConsentBanner({
  trackersConfigured,
  providers = [],
}: {
  trackersConfigured: boolean;
  providers?: string[];
}) {
  const consent = useCookieConsent((s) => s.consent);
  const settingsOpen = useCookieConsent((s) => s.settingsOpen);
  const acceptAll = useCookieConsent((s) => s.acceptAll);
  const rejectAll = useCookieConsent((s) => s.rejectAll);
  const save = useCookieConsent((s) => s.save);
  const openSettings = useCookieConsent((s) => s.openSettings);
  const closeSettings = useCookieConsent((s) => s.closeSettings);

  const showBanner = trackersConfigured && consent === null && !settingsOpen;
  if (!showBanner && !settingsOpen) return null;

  if (settingsOpen) {
    return (
      <PreferencesPanel
        initialAnalytics={consent?.analytics ?? false}
        providers={providers}
        onSave={save}
        onClose={closeSettings}
      />
    );
  }

  // First-visit banner.
  return (
    <div className="fixed inset-x-0 bottom-0 z-50 p-3 sm:p-4">
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-border bg-panel/95 p-4 shadow-2xl backdrop-blur sm:flex-row sm:items-center sm:gap-4">
        <p className="min-w-0 flex-1 text-[12.5px] leading-relaxed text-muted">
          We use strictly-necessary cookies to run the site, and — only with your consent —
          analytics tools ({joinNames(providers)}) to understand usage. See our{' '}
          <Link to="/cookies" className="text-accent hover:underline">
            Cookie Policy
          </Link>
          .
        </p>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={openSettings}
            className="text-[12px] text-faint underline-offset-2 hover:text-muted hover:underline"
          >
            Manage
          </button>
          <Button variant="ghost" onClick={rejectAll} className="h-9 px-4 text-[13px]">
            Reject
          </Button>
          <Button variant="primary" onClick={acceptAll} className="h-9 px-4 text-[13px]">
            Accept
          </Button>
        </div>
      </div>
    </div>
  );
}

function PreferencesPanel({
  initialAnalytics,
  providers,
  onSave,
  onClose,
}: {
  initialAnalytics: boolean;
  providers: string[];
  onSave: (analytics: boolean) => void;
  onClose: () => void;
}) {
  const [analytics, setAnalytics] = useState(initialAnalytics);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center sm:p-4">
      <div className="w-full max-w-lg rounded-2xl border border-border bg-panel p-5 shadow-2xl">
        <h2 className="font-display text-lg font-semibold">Cookie preferences</h2>
        <p className="mt-1 text-[12.5px] text-muted">
          Manage how SieveGuard uses cookies on this site. See the{' '}
          <Link to="/cookies" className="text-accent hover:underline" onClick={onClose}>
            Cookie Policy
          </Link>{' '}
          for details.
        </p>

        <div className="mt-4 flex flex-col gap-2">
          <Row
            title="Strictly necessary"
            desc="Required for sign-in, security, and remembering your choices. Always on."
            locked
            checked
          />
          <Row
            title="Analytics"
            desc={`${joinNames(providers)} — anonymous usage stats so we can improve the site.`}
            checked={analytics}
            onChange={setAnalytics}
          />
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="h-9 px-4 text-[13px]">
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onSave(analytics)} className="h-9 px-4 text-[13px]">
            Save preferences
          </Button>
        </div>
      </div>
    </div>
  );
}

function Row({
  title,
  desc,
  checked,
  locked,
  onChange,
}: {
  title: string;
  desc: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <label
      className={cn(
        'flex items-start gap-3 rounded-md border border-bordersoft p-3',
        locked ? 'opacity-80' : 'cursor-pointer hover:border-accentdeep',
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={locked}
        onChange={(e) => onChange?.(e.target.checked)}
        className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
      />
      <span>
        <span className="text-[13.5px] font-medium">{title}</span>
        <span className="mt-0.5 block text-[12px] text-muted">{desc}</span>
      </span>
    </label>
  );
}
